import type { TFunction } from 'i18next'

import { ApolloError } from '@apollo/client'
import tzlookup from '@photostructure/tz-lookup'

import { FacilityQueryResult } from '@/graphql/generated/hooks'
import { FacilityType } from '@/graphql/generated/schemas'
import { OptionI } from '@/utils/forms/optionBuilders'

import { LabelValueIProps } from '../types/types'
import { CSVParseResultI } from './csvParser'

export type Facility = FacilityQueryResult['data']['facility']

export const latRegex = /^\(?[+-]?(90(\.0+)?|[1-8]?\d(\.\d+)?)$/
export const lonRegex =
  /^\s?[+-]?(180(\.0+)?|1[0-7]\d(\.\d+)?|\d{1,2}(\.\d+)?)\)?$/

export const facilityTypeOptions: LabelValueIProps[] = [
  { label: 'Physical', value: FacilityType.Normal },
  { label: 'Market', value: FacilityType.Market },
]

export const validateLongitude = (
  value: string,
  type: FacilityType,
  t: TFunction
) => {
  if (type === FacilityType.Market) {
    return true
  }
  if (!value) {
    return t('facilities:crud_longitude_required')
  } else if (!lonRegex.test(value)) {
    return t('facilities:crud_longitude_invalid')
  } else return true
}

export const validateLatitude = (
  value: string,
  type: FacilityType,
  t: TFunction
) => {
  if (type === FacilityType.Market) {
    return true
  }
  if (!value) {
    return t('facilities:crud_latitude_required')
  } else if (!latRegex.test(value)) {
    return t('facilities:crud_latitude_invalid')
  } else return true
}

export const getFacilityTypeOption = (facility: Facility) => {
  return facilityTypeOptions.find((t) => t.value === facility?.type)
}

export const getFloorOptions = (facility: Facility): OptionI[] =>
  facility?.floors?.edges.map((f) => {
    return { label: f.node.name, value: f.node?.id }
  }) || []

export const getDefaultFloorOption = (facility: Facility): OptionI => {
  const defaultFloorId = facility?.defaultFloorId
  const floorOptions = getFloorOptions(facility)

  if (floorOptions.length === 0) {
    return null
  }
  return (
    floorOptions.find(({ value }) => value === defaultFloorId) ||
    floorOptions[0]
  )
}

export const getErrorMessage = (
  e: ApolloError,
  name: string,
  crudType: string
) => {
  switch (e?.graphQLErrors?.[0]?.extensions?.code) {
    case 'facility.name.not.unique':
    case 'facility.invalid.emergency.number':
      return `Error: ${e.message}`
    default:
      return `Error ${crudType} ${name} Facility`
  }
}

export const getFacilityTimezone = (lat: string, lng: string): string => {
  return tzlookup(parseFloat(lat), parseFloat(lng))
}

export const getCreateFacilityButtonTitle = (
  isBulkUpload: boolean,
  isComplete: boolean
): string => {
  if (isComplete) {
    return 'Done'
  }
  return isBulkUpload ? 'Create Facilities' : 'Create Facility'
}

export const isBulkUploadButtonDisabled = (
  parseResult: CSVParseResultI
): boolean => {
  if (!parseResult || parseResult.data.length === 0) {
    return true
  }
  return false
}
